import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
	Box,
	Divider,
	IconButton,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CanvasAddEntitySelection } from './canvasAddEntitySelection';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';

export { CanvasAddEntitySelection } from './canvasAddEntitySelection';

export interface CanvasAddElementReferenceOption {
	value: string;
	label: string;
	color: string;
}

export interface CanvasAddElementSelectButtonProps {
	value: string;
	onChange: (value: string) => void;
	/** When false, only the collection options are listed — no Note, Query, Chart, Anchor or Timeline items. */
	showCanvasElements?: boolean;
	showQueryChartGrid: boolean;
	referenceOptions: CanvasAddElementReferenceOption[];
	/** Repository ids to list before "All collections". */
	pinnedRepositoryIds: readonly string[];
}

/** Same interaction as {@link EntityTypeSelectButton}: chevron opens a menu. */
export default function CanvasAddElementSelectButton({
	onChange,
	showCanvasElements = true,
	showQueryChartGrid,
	referenceOptions,
	pinnedRepositoryIds,
}: CanvasAddElementSelectButtonProps) {
	const uid = useId();
	const buttonId = `${uid}-canvas-add-element-button`;
	const menuId = `${uid}-canvas-add-element-menu`;
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [moreExpanded, setMoreExpanded] = useState(false);
	const menuOpen = Boolean(anchorEl);

	const pinnedSet = useMemo(() => new Set(pinnedRepositoryIds), [pinnedRepositoryIds]);
	const { pinnedRefs, otherRefs } = useMemo(() => {
		const pinned: CanvasAddElementReferenceOption[] = [];
		const other: CanvasAddElementReferenceOption[] = [];
		for (const opt of referenceOptions) {
			(pinnedSet.has(opt.value) ? pinned : other).push(opt);
		}
		return { pinnedRefs: pinned, otherRefs: other };
	}, [referenceOptions, pinnedSet]);

	useEffect(() => {
		if (!menuOpen) {
			setMoreExpanded(false);
		}
	}, [menuOpen]);

	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handlePick = useCallback(
		(next: string) => {
			onChange(next);
			setAnchorEl(null);
		},
		[onChange],
	);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const triggerButton = (
		<IconButton
			id={buttonId}
			aria-controls={menuOpen ? menuId : undefined}
			aria-haspopup="true"
			aria-expanded={menuOpen ? 'true' : undefined}
			onClick={handleOpen}
			tabIndex={-1}
		>
			<ArrowDropDownIcon />
		</IconButton>
	);

	const referenceMenuItem = (option: CanvasAddElementReferenceOption) => (
		<MenuItem key={option.value} onClick={() => handlePick(option.value)} sx={{ gap: 1 }}>
			<LibraryBooksIcon fontSize="small" sx={{ color: option.color }} />
			{option.label}
		</MenuItem>
	);

	return (
		<div>
			{triggerButton}
			<Menu
				id={menuId}
				anchorEl={anchorEl}
				open={menuOpen}
				onClose={handleClose}
				MenuListProps={{
					'aria-labelledby': buttonId,
				}}
			>
				{showCanvasElements && (
					<MenuItem onClick={() => handlePick(CanvasAddEntitySelection.Note)} sx={{ gap: 1 }}>
						<StickyNote2Icon fontSize="small" sx={{ color: 'primary.main' }} />
						Note
					</MenuItem>
				)}
				{showQueryChartGrid && (
					<MenuItem onClick={() => handlePick(CanvasAddEntitySelection.Query)} sx={{ gap: 1 }}>
						<ManageSearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
						Query
					</MenuItem>
				)}
				{showQueryChartGrid && (
					<MenuItem onClick={() => handlePick(CanvasAddEntitySelection.Chart)} sx={{ gap: 1 }}>
						<ShowChartIcon fontSize="small" sx={{ color: 'primary.main' }} />
						Chart
					</MenuItem>
				)}
				{showQueryChartGrid && (
					<MenuItem onClick={() => handlePick(CanvasAddEntitySelection.Grid)} sx={{ gap: 1 }}>
						<ArchitectureIcon fontSize="small" sx={{ color: 'primary.main' }} />
						Anchor
					</MenuItem>
				)}
				{showQueryChartGrid && (
					<MenuItem onClick={() => handlePick(CanvasAddEntitySelection.Timeline)} sx={{ gap: 1 }}>
						<ViewTimelineIcon fontSize="small" sx={{ color: 'primary.main' }} />
						Timeline
					</MenuItem>
				)}
				{showCanvasElements && <Divider />}
				{showCanvasElements && (referenceOptions.length > 0 ? <Typography
					variant="caption"
					color="text.secondary"
					sx={{ px: 2, py: 0.5 }}
				>
					Data references
				</Typography> : <Typography
					variant="caption"
					color="text.secondary"
					sx={{ px: 2, py: 0.5 }}
				>
					Create some collections to add data references to your planning view
				</Typography>)}
				{pinnedRefs.map(referenceMenuItem)}
				{otherRefs.length > 0 && (
					<MenuItem
						onMouseDown={(e) => e.preventDefault()}
						onClick={(e) => {
							e.stopPropagation();
							setMoreExpanded((v) => !v);
						}}
						dense
						sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'normal' }}
					>
						{moreExpanded ? (
							<ExpandLessIcon fontSize="small" color="action" />
						) : (
							<ExpandMoreIcon fontSize="small" color="action" />
						)}
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography variant="body2" component="span" display="block">
								All collections ({otherRefs.length})
							</Typography>
						</Box>
					</MenuItem>
				)}
				{moreExpanded && otherRefs.map(referenceMenuItem)}
			</Menu>
		</div>
	);
}
